"use client"

import { useEffect, useRef, useState } from "react"

import { MAIN_SCROLLER_ID } from "@/components/layout/scroller"
import { cn } from "@/lib/utils"

/**
 * The corpus citation network: one point per document, positioned by what it cites and what
 * cites it.
 *
 * ─── Why the layout is pre-computed and the canvas only draws ──────────────────────────────
 *
 * 39,411 documents and 300,348 citations. A d3-force simulation is O(n²) per tick without
 * approximation and visibly struggles in the low thousands — the app's own CitationGraph uses
 * one, and it is sized for the handful of sources behind a single research answer, which is
 * the right tool at the wrong scale here.
 *
 * So the layout runs once, offline, in the pipeline (scripts/build_citation_layout.py, which
 * uses DrL/OpenOrd on igraph's C core) and ships as coordinates. What is left for the browser
 * is drawing tens of thousands of points ONCE onto a canvas, which is microseconds of work,
 * and that is why this is smooth on a phone while a live simulation would not be.
 *
 * The other reason to freeze it: a force layout lands somewhere different every run. A visitor
 * who comes back should see the same map, and a shape we can point at in a conversation has to
 * be a shape that stays put.
 *
 * ─── What the figure is allowed to claim ───────────────────────────────────────────────────
 *
 * It is a force layout, so distance means the same thing everywhere in it: documents are
 * pulled together by the citations between them and pushed apart otherwise. Measured on the
 * shipped layout — a citation is 0.206x the length of a random pair, 35% of a document's
 * citation partners are among its 30 nearest points on screen, and the elongated structures
 * keep 87.8% of their citations internally.
 *
 * That last number is why this replaced an SVD + t-SNE layout. t-SNE preserves local
 * neighbourhoods and discards global geometry, so it stretched groups into long sweeping arcs
 * that looked like lines of authority and were not: those kept 9.6% of their citations, and
 * position along them did not track date. The caption under this figure is written to match
 * what the layout can actually support, and should be revisited if the layout changes again.
 *
 * ─── What "interactive" is worth here ──────────────────────────────────────────────────────
 *
 * The reveal is the whole interaction, deliberately. Points fade in over about a second the
 * first time the figure scrolls into view, which reads as the structure assembling rather than
 * as a picture loading. There is no hover, no zoom and no pan: without labels a hover tells you
 * nothing, and with labels this becomes a data explorer — a different feature, on a page whose
 * job is to make someone curious for fifteen seconds.
 *
 * ─── Drawing ───────────────────────────────────────────────────────────────────────────────
 *
 * Colours are read from the stylesheet at draw time rather than hard-coded, so the figure
 * follows the theme, including a theme switched after it has already been drawn. Case law is
 * the accent and legislation is neutral: the interleaving of the two is the point of the
 * picture, and it is also the thing that distinguishes this corpus from a case-law database.
 */

/** One entry per document. Parallel arrays, not objects: 20k objects is 20k allocations to
 *  walk on every redraw, and this is read in a tight loop. */
interface NetworkData {
  generatedAt: string
  /** Layout space, as [minX, minY, maxX, maxY]. Drawing fits this to the canvas. */
  bounds: [number, number, number, number]
  x: number[]
  y: number[]
  /** Radius bucket, by citation count. Small integers, scaled at draw time. */
  s: number[]
  /** 0 = case law (judgments, AG opinions, orders), 1 = legislation and treaties. */
  t: number[]
  /** Draw rank: 0 the field, 1 the accent set (400 most-CITED), 2 a labelled landmark. */
  h: number[]
  /** Landmark documents to name. Positions are in the same space as x/y. */
  labels: { x: number; y: number; text: string; cited: number }[]
}

/** Where the pipeline writes the layout. Fetched rather than imported: a few hundred KB of
 *  coordinates does not belong in the JS bundle, and the page renders fine without it. */
const DATA_URL = "/data/citation-network.json"

const REVEAL_MS = 1100

/** Smallest dot radius, in DEVICE pixels. Below about this the anti-aliased edge is most of
 *  the dot and the whole field reads as a haze rather than as points. */
const MIN_R = 1.6

function readColour(el: HTMLElement, prop: string, fallback: string): string {
  const value = getComputedStyle(el).getPropertyValue(prop).trim()
  return value || fallback
}

/**
 * A grey `percent` of the way from the background to the foreground, resolved to something
 * canvas can use.
 *
 * The two greys in this figure have to be defined against the BACKGROUND, not written down as
 * hex. A pale grey that recedes on white is nearly invisible on near-black, so a fixed pair
 * would mean one theme reading correctly and the other not at all. Mixing from the tokens
 * makes "barely there" and "clearly there" mean the same thing in both.
 *
 * Done by letting CSS do the arithmetic — set `color-mix()` on a throwaway element and read
 * back what the browser computed, since canvas takes colour strings and cannot mix. Falls back
 * to the foreground colour if `color-mix` is unsupported, which is legible if not subtle.
 */
function mixToBackground(el: HTMLElement, percent: number): string {
  const probe = document.createElement("span")
  probe.style.display = "none"
  probe.style.color = `color-mix(in oklch, var(--foreground) ${percent}%, var(--background))`
  el.appendChild(probe)
  const resolved = getComputedStyle(probe).color
  el.removeChild(probe)
  return resolved && resolved !== "" ? resolved : readColour(el, "--foreground", "#555")
}

/**
 * Draw the landmark names.
 *
 * Twenty-two names on a square a few hundred pixels wide will collide, so placement is greedy
 * rather than fixed: the most-cited document is placed first and gets its preferred side, and
 * each later one takes the first of four candidate positions whose text box hits neither an
 * already-placed box nor the edge of the figure. A name with nowhere to go is dropped. That is
 * the right failure — an unreadable pile of overlapping words is worse than eighteen names.
 *
 * Fewer names on a narrow screen, for the same reason: the figure is the same size in layout
 * terms but every label is the same number of pixels, so the crowding is worse.
 *
 * The leader line matters. Without it a name floating near a dense patch could belong to any
 * of fifty points, and the one thing this figure must not do is invite a wrong reading.
 */
function drawLabels(
  ctx: CanvasRenderingContext2D,
  data: NetworkData,
  w: number,
  h: number,
  scale: number,
  offX: number,
  offY: number,
  colours: { ink: string; paper: string; accent: string },
) {
  if (!data.labels?.length) return

  const limit = w < 420 ? 6 : w < 640 ? 12 : data.labels.length
  const ordered = [...data.labels].sort((a, b) => b.cited - a.cited).slice(0, limit)

  const FONT_PX = w < 480 ? 9.5 : 11
  ctx.font = `500 ${FONT_PX}px var(--font-inter), ui-sans-serif, system-ui, sans-serif`
  ctx.textBaseline = "middle"

  const PAD = 4
  const GAP = 10
  /* Boxes are tested with this much slack around them. Labels that merely touch are as hard to
     read as labels that overlap, and the placer has four candidate positions to fall back on. */
  const MARGIN = 3
  const taken: { x0: number; y0: number; x1: number; y1: number }[] = []

  for (const label of ordered) {
    const px = label.x * scale + offX
    const py = label.y * scale + offY
    const tw = ctx.measureText(label.text).width
    const th = FONT_PX + PAD * 2

    // Preferred side first: away from the centre, so names radiate outwards rather than
    // crossing back over the densest part of the figure.
    const rightFirst = px >= w / 2
    const candidates = (rightFirst
      ? [[GAP, 0], [-GAP - tw, 0], [0, -GAP - th], [0, GAP + th]]
      : [[-GAP - tw, 0], [GAP, 0], [0, -GAP - th], [0, GAP + th]]) as [number, number][]

    let placed: { x0: number; y0: number; x1: number; y1: number } | null = null
    for (const [dx, dy] of candidates) {
      const x0 = px + dx - (dy === 0 ? 0 : tw / 2)
      const y0 = py + dy - th / 2
      const box = { x0: x0 - PAD, y0, x1: x0 + tw + PAD, y1: y0 + th }
      if (box.x0 < 2 || box.y0 < 2 || box.x1 > w - 2 || box.y1 > h - 2) continue
      if (taken.some((t) =>
        box.x0 - MARGIN < t.x1 && box.x1 + MARGIN > t.x0 &&
        box.y0 - MARGIN < t.y1 && box.y1 + MARGIN > t.y0)) continue
      placed = box
      break
    }
    if (!placed) continue
    taken.push(placed)

    const textX = placed.x0 + PAD
    const textY = placed.y0 + th / 2

    // Leader, from the marker to the nearest edge of the text box.
    ctx.globalAlpha = 0.45
    ctx.strokeStyle = colours.ink
    ctx.lineWidth = 0.75
    ctx.beginPath()
    ctx.moveTo(px, py)
    ctx.lineTo(textX < px ? placed.x1 - PAD : textX, textY)
    ctx.stroke()

    /* No marker ring. The landmark is already the largest, darkest dot in the figure and is
       painted in the last layer, so it needs nothing around it to be found — and the ring was
       a white circle punched into the data, which is a small lie about what is underneath. */

    /* Knock the field back behind the text. Halo rather than a filled box: a solid plate
       punches a rectangle out of the data, which is both uglier and a small lie about what is
       underneath. */
    ctx.globalAlpha = 0.85
    ctx.lineWidth = 3
    ctx.strokeStyle = colours.paper
    ctx.lineJoin = "round"
    ctx.strokeText(label.text, textX, textY)

    ctx.globalAlpha = 1
    ctx.fillStyle = colours.ink
    ctx.fillText(label.text, textX, textY)
  }
  ctx.globalAlpha = 1
}

export function CitationNetwork({
  className,
  caption,
  unavailableLabel,
  legend,
}: {
  className?: string
  caption: string
  unavailableLabel: string
  legend: { hubs: string; caseLaw: string; legislation: string }
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const [data, setData] = useState<NetworkData | null>(null)
  const [failed, setFailed] = useState(false)
  const [visible, setVisible] = useState(false)

  /*
    The layout file is optional. It is generated from the live corpus and regenerated when the
    corpus moves, so the page has to render sensibly when it is absent or half-written — during
    a deploy, or before the generator has ever been run. A missing file is a caption, not a
    crash.
  */
  useEffect(() => {
    let cancelled = false
    fetch(DATA_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((json: NetworkData) => {
        if (cancelled) return
        if (!Array.isArray(json?.x) || json.x.length === 0) throw new Error("empty")
        setData(json)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  /*
    Draw when it comes into view, not when it mounts: the figure is well below the fold, and
    the reveal is worth nothing if it plays while nobody is looking.

    Two ways of noticing, because the observer alone is not reliable enough to be the only one.
    IntersectionObserver does not deliver callbacks while the document is hidden — a background
    tab, or a preview pane that is not the front one — so a page that was scrolled into
    position while hidden can come back to the foreground with the figure in view and nothing
    drawn. The rect check covers that, and also the plain case of a short viewport where the
    figure is already on screen at load.
  */
  useEffect(() => {
    const el = wrapRef.current
    if (!el) return

    const inView = () => {
      const rect = el.getBoundingClientRect()
      return rect.top < window.innerHeight * 0.9 && rect.bottom > 0
    }
    const check = () => {
      if (!inView()) return false
      setVisible(true)
      return true
    }
    if (check()) return

    /* A second look on the next frame. The check above runs during the first render, when the
       shell has not finished laying the page out and this element's rect can still be wrong —
       and on a page that LOADS already scrolled to the figure (a refresh, a deep link) there
       may never be a scroll event to ask again. Everything else here is an event waiting to
       happen; this is the one that covers nothing happening at all. */
    const settle = requestAnimationFrame(() => {
      if (check()) cleanup()
    })

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: "-10% 0px" },
    )
    observer.observe(el)

    // The belt to the observer's braces: a scroll inside the app shell's own scrollport, and
    // the moment a hidden document becomes visible again.
    const onMaybe = () => {
      if (check()) cleanup()
    }
    const scroller = document.getElementById(MAIN_SCROLLER_ID)
    scroller?.addEventListener("scroll", onMaybe, { passive: true })
    document.addEventListener("visibilitychange", onMaybe)

    function cleanup() {
      cancelAnimationFrame(settle)
      observer.disconnect()
      scroller?.removeEventListener("scroll", onMaybe)
      document.removeEventListener("visibilitychange", onMaybe)
    }
    return cleanup
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    const wrap = wrapRef.current
    if (!canvas || !wrap || !data || !visible) return

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    let frame = 0
    let start: number | null = null
    /*
      Whether the reveal is playing, as opposed to the figure simply being drawn.

      This is the flag that keeps a half-drawn figure off the screen. The reveal advances on
      requestAnimationFrame, and rAF does not run while the document is hidden — so a reveal
      begun in a background tab freezes at whatever fraction it reached and STAYS there, which
      is worse than no animation at all. Anything that redraws for another reason (a theme
      change, a resize) has the same problem: it re-enters render() mid-reveal.

      So: animate only when someone can actually see it happen, and otherwise draw the whole
      thing at once. `revealed` latches, because the reveal is worth playing once.
    */
    let revealed = false

    /*
      Order of drawing is order of prominence, and canvas has no z-index — later simply covers
      earlier. So the whole accent set goes last, as a block, and is sorted within itself by
      size.

      Sorting by size alone was not enough. Being heavily CITED is what puts a document in the
      accent set, and that is not the same as having a large radius: plenty of hubs sit in a
      middling size bucket and were being painted over by the ordinary grey points drawn after
      them. A highlight you have to hunt for is not a highlight.

      It also improves the reveal, which walks this same array: the field fills in first and the
      accent arrives on top of it at the end, rather than the two interleaving.
    */
    /*
      Four layers, painted in this order, because canvas has no z-index — later covers earlier:

        0  case law
        1  legislation and treaties
        2  the accent set, the 400 most cited
        3  the landmarks, the ones with their names written beside them

      Sorting by size alone was not enough and sorting by rank alone was not either. The two
      greys are the bulk of the picture and they have to settle before anything meant to stand
      out is laid over them; within a layer, bigger dots still go last.
    */
    const layerOf = (i: number) =>
      data.h[i] === 2 ? 3 : data.h[i] === 1 ? 2 : data.t[i] === 0 ? 0 : 1
    const order = data.x
      .map((_, i) => i)
      .sort((a, b) => (layerOf(a) - layerOf(b)) || (data.s[a] - data.s[b]))

    const render = () => {
      const rect = wrap.getBoundingClientRect()
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const w = Math.max(1, Math.floor(rect.width))
      const h = Math.max(1, Math.floor(rect.height))
      if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.width = w * dpr
        canvas.height = h * dpr
        canvas.style.width = `${w}px`
        canvas.style.height = `${h}px`
      }
      const ctx = canvas.getContext("2d")
      if (!ctx) return

      const accent = readColour(wrap, "--accent-blue", "oklch(56.5% 0.223 263.7)")
      /* Two greys, both opaque. Case law is the quieter of the two because it is two thirds of
         the points — at equal weight it swamps the legislation it rests on and the figure stops
         having a foreground. Resolved every draw so a theme switch repaints correctly. */
      const greyCaseLaw = mixToBackground(wrap, 17)
      const greyLegislation = mixToBackground(wrap, 42)
      const ink = readColour(wrap, "--foreground", "oklch(14.5% 0 0)")
      const paper = readColour(wrap, "--background", "oklch(1 0 0)")

      const [x0, y0, x1, y1] = data.bounds
      // Fit the layout's aspect into the canvas without distorting it: a network stretched to
      // fill a wide box stops looking like the thing that was computed.
      const scale = Math.min(w / (x1 - x0), h / (y1 - y0)) * 0.94
      const offX = (w - (x1 - x0) * scale) / 2 - x0 * scale
      const offY = (h - (y1 - y0) * scale) / 2 - y0 * scale

      /*
        Drawn in DEVICE pixels, not CSS pixels, so the radius floor below can be stated in real
        pixels rather than in units that change with the display.

        The floor is what makes these read as solid. A circle with a radius near one pixel has
        a single fully-covered pixel and an anti-aliased ramp all round it, which looks
        translucent whatever the alpha is — the softness is coverage, not opacity. Snapping the
        small ones to squares fixed that and looked like what it was, so instead every dot is a
        circle and none is allowed below MIN_R.
      */
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      const elapsed = start === null ? 0 : performance.now() - start
      const progress = start === null ? 1 : Math.min(1, elapsed / REVEAL_MS)
      // Ease-out, so the field settles rather than stopping.
      const eased = 1 - Math.pow(1 - progress, 3)
      const shown = Math.floor(order.length * eased)

      ctx.globalAlpha = 1
      for (let k = 0; k < shown; k++) {
        const i = order[k]
        const rank = data.h[i]
        const px = (data.x[i] * scale + offX) * dpr
        const py = (data.y[i] * scale + offY) * dpr
        /*
          Three ranks, three treatments. The field is two greys — case law the quieter of them
          because it is two thirds of the points. The accent set is the blue. The landmarks are
          ink, which is the strongest thing the palette has and the only way a named dot wins
          against the blue around it.

          Each rank also gets a radius floor, so a document does not become a speck just
          because it happens to sit in a small size bucket.
        */
        const r =
          rank === 2 ? Math.max(3, 1.1 + data.s[i] * 0.8)
          : rank === 1 ? Math.max(1.9, 0.9 + data.s[i] * 0.8)
          : 0.75 + data.s[i] * 0.62
        ctx.fillStyle =
          rank === 2 ? ink
          : rank === 1 ? accent
          : data.t[i] === 0 ? greyCaseLaw : greyLegislation

        ctx.beginPath()
        ctx.arc(px, py, Math.max(MIN_R, r * dpr), 0, Math.PI * 2)
        ctx.fill()
      }

      // Labels measure and place in CSS pixels.
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      // Labels last, and only once the field is complete — a name arriving before the points
      // around it reads as a caption on an empty box.
      if (progress >= 1) drawLabels(ctx, data, w, h, scale, offX, offY, { ink, paper, accent })

      if (progress < 1) frame = requestAnimationFrame(render)
    }

    /** Play the reveal, once, and only when the frames will actually be delivered. */
    const beginReveal = () => {
      if (revealed || reduced || document.visibilityState !== "visible") return false
      revealed = true
      start = performance.now()
      frame = requestAnimationFrame(render)
      return true
    }

    // `start` stays null unless the reveal is running, and a null start means progress 1 — so
    // every other path through render(), including this first one, draws the complete figure.
    if (!beginReveal()) render()

    /* A tab that was hidden when the figure came into view gets its reveal when it comes
       forward. If it has already been revealed, this does nothing and the drawn figure stands. */
    const onVisible = () => {
      beginReveal()
    }
    document.addEventListener("visibilitychange", onVisible)

    /* Redraw on resize and on a theme change. The theme one matters because the colours are
       read from the stylesheet: without it, switching to dark leaves the figure drawn in the
       light palette on a dark background. */
    const onResize = () => render()
    window.addEventListener("resize", onResize)
    const themeObserver = new MutationObserver(() => render())
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-theme"],
    })

    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener("resize", onResize)
      document.removeEventListener("visibilitychange", onVisible)
      themeObserver.disconnect()
    }
  }, [data, visible])

  return (
    <figure className={cn("flex w-full flex-col gap-6", className)}>
      {/*
        The layout is square and is fitted without distortion, so the drawing is only ever as
        wide as the box is tall — width and height are the same number here. Stretching it to
        fill a wide band instead would mean drawing something other than what was computed.

        It grows with the viewport rather than sitting at one size, because the figure is
        39,411 points and what limits how much of it can be READ is how many pixels each
        cluster gets.

        Note the breakpoints are this project's, not Tailwind's: globals.css sets lg to 1280 and
        xl to 1536, so a 1440px laptop is on `lg` and never reaches `xl`. Stepping at Tailwind's
        defaults would have left every normal laptop at the base size.

        The `90vh` term is the other half of it. The box is square, so every pixel of width is
        also a pixel of height, and an unbounded 1040px square on a short laptop screen is
        taller than the window. It is set loose rather than tight — the figure sits under a
        heading and a paragraph and so is never fully above the fold regardless, and a cap
        strict enough to change that would hold the figure near its base size on every laptop
        made.

        It caps the WIDTH rather than the height, which looks like the wrong axis and is not.
        `max-h` with `aspect-square` clamps the height and leaves the width alone — the box
        stops being square and the figure, which is fitted without distortion, ends up as a
        small square in the middle of a wide band with empty margins either side. Capping the
        width by viewport height keeps the box square and shrinks the whole thing.
      */}
      <div
        ref={wrapRef}
        className="relative mx-auto aspect-square w-full max-w-[680px] lg:max-w-[min(900px,90vh)] xl:max-w-[min(1040px,90vh)]"
      >
        <canvas ref={canvasRef} className="h-full w-full" aria-hidden />
        {failed && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-[13px] font-[350] text-muted-foreground">{unavailableLabel}</p>
          </div>
        )}
      </div>

      <figcaption className="flex flex-col gap-3">
        <div className="flex flex-row flex-wrap items-center gap-x-6 gap-y-2">
          {/* Three keys, because the figure now draws three things. The swatches mix from the
              same tokens and at the same percentages as the canvas, so a key never promises a
              shade the figure does not use. */}
          <LegendKey label={legend.hubs} className="bg-[var(--accent-blue)]" />
          <LegendKey
            label={legend.caseLaw}
            className="bg-[color-mix(in_oklch,var(--foreground)_26%,var(--background))]"
          />
          <LegendKey
            label={legend.legislation}
            className="bg-[color-mix(in_oklch,var(--foreground)_62%,var(--background))]"
          />
        </div>
        <p className="max-w-2xl text-[13px] font-[350] leading-relaxed text-muted-foreground">
          {caption}
        </p>
      </figcaption>
    </figure>
  )
}

function LegendKey({ label, className }: { label: string; className: string }) {
  return (
    <span className="flex flex-row items-center gap-2">
      <span className={cn("size-2 shrink-0 rounded-full", className)} />
      <span className="text-[13px] font-[350] text-muted-foreground">{label}</span>
    </span>
  )
}
